import React, { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, formatPrice, pricing, standardPricingFrom } from '../siteConfig';
import { trackEvent } from '../services/analytics';

// The booking modal is provided once in App.js. Without a provider, the
// booking button does nothing instead of crashing.
export const BookCallContext = createContext({ open: () => {} });
export const useBookCall = () => useContext(BookCallContext);

export const cn = (...classes) => classes.filter(Boolean).join(' ');

export const Container = ({ className = '', children }) => (
  <div className={cn('mx-auto w-full max-w-6xl px-5 md:px-8', className)}>{children}</div>
);

export const Section = ({ id, className = '', children }) => (
  <section id={id} className={cn('relative py-16 md:py-24', className)}>{children}</section>
);

export const Eyebrow = ({ children, className = '', size = 'sm' }) => (
  <p className={cn('font-mono-accent uppercase tracking-[0.28em] text-gold-light', size === 'lg' ? 'text-[13px]' : 'text-[11px]', className)}>{children}</p>
);

export const Pill = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1 text-xs text-dust">
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, description, align = 'left', className = '' }) => (
  <div className={cn(align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl', className)}>
    {eyebrow ? <Eyebrow className="mb-3">{eyebrow}</Eyebrow> : null}
    <h2 className="font-display text-3xl leading-tight text-cream md:text-4xl">{title}</h2>
    {description ? <p className="mt-4 text-base leading-7 text-dust md:text-lg">{description}</p> : null}
  </div>
);

const buttonStyles = {
  primary: 'bg-gold text-ink hover:bg-gold-light shadow-gold hover:shadow-gold-lg',
  secondary: 'border border-white/[0.1] bg-white/[0.03] text-cream hover:border-gold/40 hover:text-gold-light',
  ghost: 'text-dust hover:text-gold-light',
};

const buttonSizes = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
  lg: 'px-7 py-3.5 text-base',
};

export const ButtonLink = ({ to, href, children, variant = 'primary', size = 'md', className = '', onClick, ...rest }) => {
  const { open: openBookCall } = useBookCall();
  const classes = cn(
    'inline-flex items-center justify-center gap-2 rounded-2xl font-display font-medium transition-all duration-200 hover:-translate-y-0.5',
    buttonStyles[variant],
    buttonSizes[size],
    className
  );

  // Hide every "hear the demo" link until a demo number is configured.
  if (to === '/demo' && !siteConfig.demoPhone) return null;

  const handleClick = (event) => {
    if (onClick) onClick(event);
    if (to) trackEvent('cta_click', { to });
    if (href && href.startsWith('tel:')) trackEvent('phone_click', { link: href });
    if (href && href.startsWith('mailto:')) trackEvent('email_click', { link: href });
    if (href && !href.startsWith('tel:') && !href.startsWith('mailto:')) trackEvent('outbound_click', { link: href });
  };

  // All "contact" buttons open the Book a call modal instead of a page.
  if (to === '/contact') {
    return (
      <button
        type="button"
        className={classes}
        onClick={(event) => {
          handleClick(event);
          openBookCall({ source: window.location.pathname });
        }}
        {...rest}
      >
        {children}
      </button>
    );
  }

  if (to) {
    return <Link to={to} className={classes} onClick={handleClick} {...rest}>{children}</Link>;
  }

  return <a href={href} className={classes} onClick={handleClick} {...rest}>{children}</a>;
};

export const Card = ({ className = '', children }) => (
  <div className={cn('rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 shadow-2xl shadow-ink/20', className)}>
    {children}
  </div>
);

export const AnswerBlock = ({ children, className = '' }) => (
  <div className={cn('rounded-r-2xl border-l-4 border-gold bg-white/[0.04] px-5 py-5 text-base leading-7 text-dust md:text-lg', className)}>
    {children}
  </div>
);

export const FeatureList = ({ items, className = '' }) => (
  <ul className={cn('space-y-3', className)}>
    {items.map((item) => (
      <li key={item} className="flex gap-3 text-sm leading-6 text-dust">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const FAQList = ({ items, className = '' }) => (
  <div className={cn('divide-y divide-white/[0.06] rounded-3xl border border-white/[0.06] bg-white/[0.02]', className)}>
    {items.map((item) => (
      <details key={item.question} className="group px-5 py-4 md:px-6">
        <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-medium text-cream">
          <span>{item.question}</span>
          <span className="mt-1 text-gold transition-transform duration-200 group-open:rotate-45">+</span>
        </summary>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-dust">{item.answer}</p>
      </details>
    ))}
  </div>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-xs text-ash">
    {items.map((item, index) => (
      <React.Fragment key={item.name}>
        {index > 0 ? <span>/</span> : null}
        {item.to ? (
          <Link to={item.to} className="hover:text-gold-light">{item.name}</Link>
        ) : (
          <span className="text-dust">{item.name}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

export const DemoCallout = ({ compact = false }) => {
  // The demo callout only appears once a demo number is configured.
  if (!siteConfig.demoPhone) return null;

  return (
    <div className={cn('rounded-3xl border border-gold/20 bg-gradient-to-br from-gold/10 to-white/[0.02] p-6 md:p-8', compact ? '' : 'md:p-10')}>
      <Eyebrow>Live demo</Eyebrow>
      <h3 className="mt-3 font-display text-2xl text-cream md:text-3xl">Hear the AI answer a real call.</h3>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-dust md:text-base">
        Ask for a quote, book a job, or ask a question it should not answer. The demo is the fastest way to judge whether this fits your business.
      </p>
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <ButtonLink href={`tel:${siteConfig.demoPhone.replace(/\s+/g, '')}`} onClick={() => trackEvent('demo_call_click', { source: 'demo_callout' })}>
          Call {siteConfig.demoPhone}
        </ButtonLink>
        <ButtonLink to="/demo" variant="secondary">What to try</ButtonLink>
      </div>
    </div>
  );
};

export const PriceCard = ({ offer, featured = false }) => {
  const price = { ...offer, ...(pricing[offer.key] || {}) };
  const setup = formatPrice(price.setup);
  const monthly = formatPrice(price.monthly);
  const standardSetup = formatPrice(price.standardSetup);
  const standardMonthly = formatPrice(price.standardMonthly);
  const isVoice = offer.key === 'websiteChatbotVoice';
  const proMonthly = formatPrice(price.proMonthly);
  const proStandardMonthly = formatPrice(price.proStandardMonthly);
  const overage = price.overage ? `$${Number(price.overage).toFixed(2)}` : null;
  const proOverage = price.proOverage ? `$${Number(price.proOverage).toFixed(2)}` : null;

  return (
    <div className={cn('flex h-full flex-col rounded-3xl border p-6', featured ? 'border-gold/40 bg-gold/[0.06]' : 'border-white/[0.06] bg-white/[0.02]')}>
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-display text-xl text-cream">{offer.name}</h3>
        {featured ? <span className="rounded-full bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink">Popular</span> : null}
      </div>
      <p className="mt-3 text-sm leading-6 text-dust">{offer.description}</p>
      <p className="mt-5 font-display text-lg text-gold-light">
        Special price: {setup} setup + {monthly}/month
      </p>
      {isVoice ? (
        <p className="mt-1 text-sm text-dust">1,000 mins: {proMonthly}/month</p>
      ) : null}
      <p className="mt-2 text-xs leading-5 text-ash">
        From {standardPricingFrom}: {standardSetup} setup + {standardMonthly}/month
        {isVoice ? ` (1,000 mins: ${proStandardMonthly}/month)` : ''}
      </p>
      {isVoice ? (
        <p className="mt-2 text-xs leading-5 text-ash">
          Extra minutes cost {overage}/min on 300 mins; {proOverage}/min on 1,000 mins.
        </p>
      ) : null}
      <FeatureList items={offer.includes} className="mt-5 flex-1" />
      <p className="mt-5 border-t border-white/[0.06] pt-4 text-xs leading-5 text-ash">Good if: {offer.bestFor}</p>
      <div className="mt-5">
        <ButtonLink to="/contact" variant={featured ? 'primary' : 'secondary'} size="sm" className="w-full">
          Ask about this bundle
        </ButtonLink>
      </div>
    </div>
  );
};

export const People = ({ items }) => (
  <div className="grid gap-4 md:grid-cols-3">
    {items.map((item) => (
      <Card key={item.title}>
        <h3 className="font-display text-xl text-cream">{item.title}</h3>
        <p className="mt-3 text-sm leading-6 text-dust">{item.description}</p>
      </Card>
    ))}
  </div>
);

export const Prose = ({ children, className = '' }) => (
  <div className={cn(
    'max-w-3xl space-y-4 text-base leading-7 text-dust',
    '[&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-cream',
    '[&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-xl [&_h3]:text-cream',
    '[&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5',
    '[&_a]:text-gold-light [&_a]:underline-offset-4 hover:[&_a]:underline',
    className
  )}>
    {children}
  </div>
);

export const CTASection = ({ title = 'Ready to stop missing enquiries?', description = 'Tell us what your business does and where the calls or leads are getting lost. We will tell you what fits, and what does not.', primaryTo = '/contact', primaryLabel = 'Book a call', secondaryTo = '/pricing', secondaryLabel = 'See pricing' }) => (
  <Section className="border-t border-white/[0.04]">
    <Container>
      <div className="rounded-[2rem] border border-gold/20 bg-gradient-to-br from-gold/10 via-white/[0.03] to-transparent p-8 md:p-12">
        <div className="max-w-3xl">
          <Eyebrow>Next step</Eyebrow>
          <h2 className="mt-3 font-display text-3xl leading-tight text-cream md:text-4xl">{title}</h2>
          <p className="mt-4 text-base leading-7 text-dust md:text-lg">{description}</p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <ButtonLink to={primaryTo}>{primaryLabel}</ButtonLink>
            {secondaryTo ? <ButtonLink to={secondaryTo} variant="secondary">{secondaryLabel}</ButtonLink> : null}
          </div>
        </div>
      </div>
    </Container>
  </Section>
);

export const PageHero = ({ eyebrow, title, description, children, breadcrumbs, compact = false }) => (
  <section className={cn('relative overflow-hidden border-b border-white/[0.04]', compact ? 'py-14 md:py-20' : 'py-16 md:py-24')}>
    <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-gold/10 blur-[140px]" />
    <Container className="relative">
      {breadcrumbs ? <Breadcrumbs items={breadcrumbs} /> : null}
      <div className="max-w-4xl">
        {eyebrow ? <Eyebrow className="mb-4" size="lg">{eyebrow}</Eyebrow> : null}
        <h1 className="font-display text-4xl leading-[1.08] tracking-tight text-cream md:text-5xl lg:text-6xl">{title}</h1>
        {description ? <p className="mt-5 max-w-2xl text-base leading-8 text-dust md:text-lg">{description}</p> : null}
        {children ? <div className="mt-8">{children}</div> : null}
      </div>
    </Container>
  </section>
);

export const StatGrid = ({ items }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((item) => (
      <div key={item.label} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <p className="font-display text-2xl text-gold-light">{item.value}</p>
        <p className="mt-1 text-sm leading-6 text-dust">{item.label}</p>
      </div>
    ))}
  </div>
);
